import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { Component, DestroyRef, inject, Input, OnInit, SimpleChanges } from '@angular/core';
import { MatError, MatFormField, MatHint, MatInput, MatLabel } from '@angular/material/input';
import { createUserDetailsForm, Party } from '../../models/user-details-form';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { EllipsisTooltipDirective } from '../../../../../../../../directives/ellipsis-tooltip.directive';
import { PartySelectorComponent } from '../party-selector/party-selector.component';
import { MatOption, MatSelect } from '@angular/material/select';
import { UserService } from '../../../../../../../../services/user.service';
import { UsersService } from '../../../../services/users.service';
import { catchError, debounceTime, distinctUntilChanged, filter, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const EMAIL_EXISTS_ERROR_KEY = 'emailExists';
const EMAIL_CHECK_DEBOUNCE_MS = 300;

@Component({
  selector: 'app-basic-information',
  standalone: true,
  templateUrl: './basic-information.component.html',
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatError,
    MatIcon,
    MatCheckbox,
    EllipsisTooltipDirective,
    PartySelectorComponent,
    MatSelect,
    MatOption,
    MatHint
  ],
  styleUrl: './basic-information.component.scss'
})
export class BasicInformationComponent implements OnInit {
  @Input() group = createUserDetailsForm();

  isAdmin = false;

  private userService = inject(UserService);
  private usersService = inject(UsersService);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.group
      .get('id')
      ?.valueChanges.pipe(
        filter(Boolean),
        switchMap(() => this.userService.isUserAdmin()),
        tap(isAdmin => {
          this.isAdmin = isAdmin;
          if (this.isAdmin) {
            this.group.get('userType')?.enable();
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.setupEmailDuplicateCheck();
  }

  onMainPartyCloseClick() {
    this.group.get('mainPartyName')?.setValue('');
    this.group.get('mainPartyNumber')?.setValue('');
  }

  onPartySelected(party: Party | null) {
    this.group.get('mainPartyName')?.setValue(party?.name ?? '');
    this.group.get('mainPartyNumber')?.setValue(party?.number ?? '');
  }

  private setupEmailDuplicateCheck() {
    const emailControl = this.group.get('email');
    if (!emailControl) {
      return;
    }

    // Clears the duplicate warning as soon as the admin edits the email, independent of the
    // debounced re-check below, so the warning never lingers on a value that no longer matches it.
    emailControl.valueChanges
      .pipe(
        tap(() => this.clearEmailExistsError(emailControl)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    emailControl.valueChanges
      .pipe(
        debounceTime(EMAIL_CHECK_DEBOUNCE_MS),
        distinctUntilChanged(),
        filter(() => !emailControl.disabled && emailControl.valid),
        switchMap(email => this.usersService.checkEmailExists(email).pipe(catchError(() => of({ exists: false })))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ exists }) => {
        if (exists) {
          emailControl.setErrors({ ...emailControl.errors, [EMAIL_EXISTS_ERROR_KEY]: true });
        }
      });
  }

  private clearEmailExistsError(control: AbstractControl) {
    if (!control.hasError(EMAIL_EXISTS_ERROR_KEY)) {
      return;
    }

    const remainingErrors = Object.fromEntries(
      Object.entries(control.errors ?? {}).filter(([key]) => key !== EMAIL_EXISTS_ERROR_KEY)
    );
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }
}
